import { useEffect, useId, useMemo, useState } from "react";
import { Input, Modal } from "@iq/models";
import { createProblemFromIncident, getProblems, linkIncidentToProblem } from "../../services/ticket_service";
import "./AssignProblemModal.css";

const MODE_OPTIONS = [
    { value: "link_existing", label: "Link Existing" },
    { value: "create_new", label: "Create New" },
];

const OPEN_PROBLEM_STATUSES = new Set(["new", "investigating", "known_error", "fix_in_progress"]);
const STATUS_OPTIONS = ["new", "investigating", "known_error", "fix_in_progress", "resolved", "closed", "cancelled"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

function formatTicketLabel(problem) {
    const number = problem?.number || `PRB-${problem?.id ?? "?"}`;
    const title = problem?.title || "Untitled Problem";
    return `${number} - ${title}`;
}

function buildAssigneeLabel(member) {
    return member?.name || member?.email || `User #${member?.id ?? "?"}`;
}

export default function AssignProblemModal({
    isOpen,
    onClose,
    incident,
    teamMembers = [],
    onCompleted,
}) {
    const formId = useId();
    const [mode, setMode] = useState("link_existing");
    const [selectedProblemId, setSelectedProblemId] = useState("");
    const [loadingProblems, setLoadingProblems] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [problemSearch, setProblemSearch] = useState("");
    const [problems, setProblems] = useState([]);
    const [openProblemAfterSubmit, setOpenProblemAfterSubmit] = useState(true);
    const [formData, setFormData] = useState({
        assigned_to_id: incident?.assigned_to_id != null ? String(incident.assigned_to_id) : "",
        title: incident?.title || "",
        description: incident?.description || "",
        status: "new",
        priority: incident?.priority || "medium",
        root_cause: "",
        workaround: "",
    });

    const assigneeOptions = useMemo(
        () =>
            (Array.isArray(teamMembers) ? teamMembers : [])
                .map((member) => ({ value: String(member?.id ?? ""), label: buildAssigneeLabel(member) }))
                .filter((option) => option.value !== ""),
        [teamMembers]
    );

    const filteredOpenProblems = useMemo(() => {
        const search = problemSearch.trim().toLowerCase();

        return (Array.isArray(problems) ? problems : [])
            .filter((problem) => OPEN_PROBLEM_STATUSES.has(String(problem?.status || "").toLowerCase()))
            .filter((problem) => {
                if (!search) {
                    return true;
                }

                return [problem?.number, problem?.title, problem?.description]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(search));
            })
            .sort((left, right) => new Date(right?.created_at || 0) - new Date(left?.created_at || 0));
    }, [problemSearch, problems]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setMode("link_existing");
        setSelectedProblemId("");
        setProblemSearch("");
        setError("");
        setSaving(false);
        setOpenProblemAfterSubmit(true);
        setLoadingProblems(true);
        setFormData({
            assigned_to_id: incident?.assigned_to_id != null ? String(incident.assigned_to_id) : "",
            title: incident?.title || "",
            description: incident?.description || "",
            status: "new",
            priority: incident?.priority || "medium",
            root_cause: "",
            workaround: "",
        });

        let cancelled = false;

        (async () => {
            try {
                const loadedProblems = await getProblems({ limit: 200 });
                if (!cancelled) {
                    setProblems(Array.isArray(loadedProblems) ? loadedProblems : []);
                }
            } catch (problemError) {
                if (!cancelled) {
                    setProblems([]);
                    setError(problemError?.message || "Failed to load problems");
                }
            } finally {
                if (!cancelled) {
                    setLoadingProblems(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [incident, isOpen]);

    const selectedProblem = useMemo(
        () => filteredOpenProblems.find((problem) => String(problem?.id) === String(selectedProblemId)),
        [filteredOpenProblems, selectedProblemId]
    );

    const canSubmit = useMemo(() => {
        if (saving || !incident?.id) {
            return false;
        }

        if (mode === "link_existing") {
            return Boolean(selectedProblemId);
        }

        return Boolean(formData.title.trim());
    }, [formData.title, incident?.id, mode, saving, selectedProblemId]);

    const actionButtons = useMemo(
        () => [
            {
                key: "cancel-assign-problem",
                text: "Cancel",
                color: "light",
                onClick: onClose,
                disabled: saving,
            },
            {
                key: "submit-assign-problem",
                text: saving ? (mode === "link_existing" ? "Linking..." : "Creating...") : (mode === "link_existing" ? "Link Problem" : "Create Problem"),
                color: "accent",
                type: "submit",
                form: formId,
                disabled: !canSubmit,
            },
        ],
        [canSubmit, formId, mode, onClose, saving]
    );

    function handleFieldChange(event) {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError("");
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (!incident?.id) {
            return;
        }

        setSaving(true);
        setError("");

        try {
            if (mode === "link_existing") {
                await linkIncidentToProblem(incident.id, Number(selectedProblemId));
                onCompleted?.({
                    mode,
                    incidentId: incident.id,
                    problemId: Number(selectedProblemId),
                    problem: selectedProblem || null,
                    openProblem: openProblemAfterSubmit,
                });
                onClose?.();
                return;
            }

            const payload = {
                assigned_to_id: formData.assigned_to_id ? Number(formData.assigned_to_id) : null,
                title: formData.title.trim(),
                description: formData.description.trim(),
                status: formData.status || "new",
                priority: formData.priority || "medium",
                root_cause: formData.root_cause.trim(),
                workaround: formData.workaround.trim(),
            };

            const createdPayload = await createProblemFromIncident(incident.id, payload);
            onCompleted?.({
                mode,
                incidentId: incident.id,
                problemId: Number(createdPayload?.problem?.id || 0) || null,
                problem: createdPayload?.problem || null,
                openProblem: openProblemAfterSubmit,
            });
            onClose?.();
        } catch (submitError) {
            setError(submitError?.message || "Failed to assign problem");
        } finally {
            setSaving(false);
        }
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Assign Problem"
            actionButtons={actionButtons}
            errorMessage={error}
            tall
        >
            <form id={formId} onSubmit={handleSubmit} className="assign-problem-modal-form">
                        <div className="assign-problem-mode-selector">
                            {MODE_OPTIONS.map((option) => (
                                <div
                                    key={option.value}
                                    className={`assign-problem-mode-selector-option ${mode === option.value ? "assign-problem-mode-selector-option-active" : ""}`}
                                    onClick={() => {
                                        setMode(option.value);
                                        setError("");
                                    }}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>

                        <label className="assign-problem-open-toggle" htmlFor="assign-problem-open-after-submit">
                            <input
                                id="assign-problem-open-after-submit"
                                type="checkbox"
                                checked={openProblemAfterSubmit}
                                onChange={(event) => setOpenProblemAfterSubmit(event.target.checked)}
                                disabled={saving}
                            />
                            <span>Open problem after submit</span>
                        </label>

                {mode === "link_existing" ? (
                    <div className="assign-problem-existing-panel">
                        <div className="">
                            <label htmlFor="problem-search">Search Problems</label>
                            <Input
                                id="problem-search"
                                type="search"
                                name="problem-search"
                                value={problemSearch}
                                onChange={(event) => setProblemSearch(event.target.value)}
                                placeholder="Search by problem number, title, or description"
                                disabled={loadingProblems || mode !== "link_existing"}
                                colorScheme="light"
                            />
                        </div>
                        <div className="assign-problem-panel-title-row">
                            <h3>Open Problems</h3>
                            <span>{filteredOpenProblems.length} found</span>
                        </div>
                        <div className="assign-problem-list" role="listbox" aria-label="Open problems">
                            {loadingProblems ? (
                                <div className="modal-empty-state">Loading problems...</div>
                            ) : filteredOpenProblems.length === 0 ? (
                                <div className="modal-empty-state">No open problems found.</div>
                            ) : (
                                filteredOpenProblems.map((problem) => {
                                    const isSelected = String(selectedProblemId) === String(problem?.id);

                                    return (
                                        <button
                                            key={problem.id}
                                            type="button"
                                            className={[
                                                "assign-problem-list-item",
                                                isSelected ? "assign-problem-list-item--selected" : "",
                                            ].filter(Boolean).join(" ")}
                                            onClick={() => {
                                                setSelectedProblemId(String(problem.id));
                                                setError("");
                                            }}
                                        >
                                            <div className="assign-problem-list-item__headline">{problem?.title}</div>
                                            <div className="assign-problem-list-item__meta">{problem.number}</div>
                                            <div className="assign-problem-list-item__meta">
                                                <span>Status: {problem?.status || "-"}</span>
                                                <span>Priority: {problem?.priority || "-"}</span>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                        {selectedProblem ? (
                            <div className="assign-problem-selected-summary">
                                Selected: {formatTicketLabel(selectedProblem)}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className="form-fields-container assign-problem-create-grid">
                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="assign-problem-assigned-to">Assigned To</label>
                            <Input
                                id="assign-problem-assigned-to"
                                type="dropdown"
                                value={assigneeOptions.find((option) => String(option.value) === String(formData.assigned_to_id))?.label || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={assigneeOptions}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, assigned_to_id: String(option.value) }))}
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="assign-problem-title">Title *</label>
                            <Input
                                id="assign-problem-title"
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleFieldChange}
                                placeholder="Problem title"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="assign-problem-description">Description</label>
                            <Input
                                id="assign-problem-description"
                                type="textarea"
                                name="description"
                                value={formData.description}
                                onChange={handleFieldChange}
                                placeholder="Problem description"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field modal-form-field-skinny">
                            <label htmlFor="assign-problem-status">Status</label>
                            <Input
                                id="assign-problem-status"
                                type="dropdown"
                                value={STATUS_OPTIONS.find((option) => option === formData.status)?.toString() || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={STATUS_OPTIONS.map((option) => ({ value: option, label: option }))}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, status: option.value }))}
                            />
                        </div>

                        <div className="modal-form-field modal-form-field-skinny">
                            <label htmlFor="assign-problem-priority">Priority</label>
                            <Input
                                id="assign-problem-priority"
                                type="dropdown"
                                value={PRIORITY_OPTIONS.find((option) => option === formData.priority)?.toString() || ""}
                                allowCustom={false}
                                colorScheme="light"
                                renderInPortal
                                options={PRIORITY_OPTIONS.map((option) => ({ value: option, label: option }))}
                                onOptionSelect={(option) => setFormData((prev) => ({ ...prev, priority: option.value }))}
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="assign-problem-root-cause">Root Cause</label>
                            <Input
                                id="assign-problem-root-cause"
                                type="textarea"
                                name="root_cause"
                                value={formData.root_cause}
                                onChange={handleFieldChange}
                                placeholder="Root cause"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="assign-problem-workaround">Workaround</label>
                            <Input
                                id="assign-problem-workaround"
                                type="textarea"
                                name="workaround"
                                value={formData.workaround}
                                onChange={handleFieldChange}
                                placeholder="Workaround"
                                disabled={saving}
                                colorScheme="light"
                            />
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
}